import React, { useState } from 'react';

/**
 * SVG Pie Chart component
 * @param {Object} props
 * @param {Array} props.data - Array of { label, value, color }
 * @param {number} props.size - Chart diameter in pixels (default 200)
 * @param {string} props.title - Optional chart title
 */
function PieChart({ data, size = 200, title, onSliceClick, valueFormatter }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const total = data.reduce((sum, d) => sum + d.value, 0);

  if (total === 0 || data.length === 0) {
    return (
      <div className="flex flex-col items-center">
        {title && <h4 className="text-sm font-semibold text-gray-600 mb-2">{title}</h4>}
        <div className="flex items-center justify-center text-gray-400 text-sm" style={{ width: size, height: size }}>
          No data
        </div>
      </div>
    );
  }

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 10;
  let cumulativeAngle = -90; // Start from top

  const slices = data.filter(d => d.value > 0).map((d, i) => {
    const angle = (d.value / total) * 360;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;
    cumulativeAngle = endAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    // For single-slice case (100%), draw a full circle
    if (data.filter(d => d.value > 0).length === 1) {
      return {
        ...d,
        path: `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 1 ${cx - 0.01} ${cy - radius} Z`,
        percentage: 100,
        midAngle: 0,
        index: i
      };
    }

    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const midAngle = ((startAngle + endAngle) / 2 * Math.PI) / 180;

    return {
      ...d,
      path,
      percentage: ((d.value / total) * 100).toFixed(1),
      midAngle,
      index: i
    };
  });

  return (
    <div className="flex flex-col items-center">
      {title && <h4 className="text-sm font-semibold text-gray-600 mb-2">{title}</h4>}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {slices.map((slice, i) => (
            <path
              key={i}
              d={slice.path}
              fill={slice.color}
              stroke="white"
              strokeWidth="2"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onSliceClick && onSliceClick(slice)}
              style={{
                opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.6 : 1,
                transition: 'opacity 0.2s, transform 0.2s',
                transformOrigin: `${cx}px ${cy}px`,
                transform: hoveredIndex === i ? 'scale(1.05)' : 'scale(1)',
                cursor: onSliceClick ? 'pointer' : 'default'
              }}
            />
          ))}
          {/* Center hole for donut effect */}
          <circle cx={cx} cy={cy} r={radius * 0.5} fill="white" />
          {hoveredIndex !== null && slices[hoveredIndex] && (
            <>
              <text x={cx} y={cy - 6} textAnchor="middle" className="text-xs font-bold fill-gray-700" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {slices[hoveredIndex].percentage}%
              </text>
              <text x={cx} y={cy + 10} textAnchor="middle" className="text-xs fill-gray-500" style={{ fontSize: '10px' }}>
                {slices[hoveredIndex].label}
              </text>
            </>
          )}
        </svg>
        {/* Legend */}
        <div className="flex flex-col gap-1.5">
          {slices.map((slice, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-xs ${onSliceClick ? 'cursor-pointer hover:underline' : 'cursor-pointer'}`}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onSliceClick && onSliceClick(slices[i])}
            >
              <span
                className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: slice.color }}
              />
              <span className="text-gray-600 whitespace-nowrap">
                {slice.label}: {valueFormatter ? valueFormatter(slice.value) : slice.value} ({slice.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PieChart;
